"use client";

import { MotionView, MotionFadeUp } from "@/components/ui/motion-wrapper";

const defaultClients = [
  { name: "TechVentures India", industry: "Technology" },
  { name: "DataFlow Inc", industry: "Analytics" },
  { name: "FinEdge", industry: "Fintech" },
  { name: "MediCare Plus", industry: "Healthcare" },
  { name: "RetailMax", industry: "E-Commerce" },
  { name: "LogiTrack", industry: "Logistics" },
  { name: "EduNxt", industry: "Education" },
  { name: "CloudFirst", industry: "SaaS" },
];

export function ClientLogos({
  eyebrow,
  heading = "Trusted by Leading Companies",
  description,
  clients = defaultClients,
  showNames = true,
}: {
  eyebrow?: string;
  heading?: string;
  description?: string;
  clients?: Array<{ name: string; industry?: string; logo?: string | null; logoAlt?: string; href?: string; description?: string | null }>;
  showNames?: boolean;
}) {
  const items = clients.length > 0 ? [...clients, ...clients] : [];

  return (
    <section className="overflow-hidden border-y border-border bg-muted/35 py-14 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <MotionView>
          <MotionFadeUp>
            <div className="mx-auto mb-8 max-w-3xl text-center">
              {eyebrow ? <p className="mb-3 text-sm font-semibold uppercase tracking-[0.24em] text-primary">{eyebrow}</p> : null}
              <h2 className="text-3xl font-bold text-foreground lg:text-5xl">{heading}</h2>
              {description ? <p className="mt-3 text-base text-muted-foreground lg:text-lg">{description}</p> : null}
            </div>
          </MotionFadeUp>
          <MotionFadeUp>
            <div className="relative overflow-hidden rounded-[30px] border border-border/70 bg-background/75 px-3 py-3 shadow-[0_18px_45px_-28px_rgba(15,23,42,0.18)] backdrop-blur">
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-background via-background/88 to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-background via-background/88 to-transparent" />
              <div className="flex min-w-max items-center gap-3 [animation:clientMarquee_42s_linear_infinite] hover:[animation-play-state:paused]">
                {items.map((client, index) => {
                  const content = (
                    <div className="group flex min-w-[156px] items-center gap-3 rounded-2xl border border-border/60 bg-card/90 px-3.5 py-3 text-card-foreground shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/25 hover:bg-card">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-muted ring-1 ring-border/70">
                        {client.logo ? (
                          <img src={client.logo} alt={client.logoAlt || client.name} className="h-8 w-8 object-contain opacity-90 transition-opacity duration-300 group-hover:opacity-100" />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-xs font-bold text-primary">
                            {client.name.split(" ").map((word) => word[0]).join("").slice(0, 2)}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        {showNames ? <p className="truncate text-sm font-semibold text-foreground/90">{client.name}</p> : <span className="block h-2 w-16 rounded-full bg-muted-foreground/20" />}
                      </div>
                    </div>
                  );

                  return client.href ? <a key={`${client.name}-${index}`} href={client.href} target="_blank" rel="noreferrer">{content}</a> : <div key={`${client.name}-${index}`}>{content}</div>;
                })}
              </div>
            </div>
          </MotionFadeUp>
        </MotionView>
      </div>
      <style jsx>{`
        @keyframes clientMarquee {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </section>
  );
}
